import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ClipboardDocumentCheckIcon,
  MegaphoneIcon,
  UserCircleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import Table from "../../components/Table/Table";
import SpinningWheel from "../../components/SpinningWheel/SpinningWheel";
import ProspectsService from "../../services/ProspectsService";
import Swal from "sweetalert2";

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className={`bg-white rounded-xl shadow p-6 flex items-center gap-4 border-l-4 ${color}`}>
    <div className="bg-slate-100 p-3 rounded-full">
      <Icon className="h-8 w-8 text-gray-600" />
    </div>
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-3xl font-bold text-gray-800">{value}</p>
    </div>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-1 text-sm text-slate-700">{value || "-"}</p>
  </div>
);

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;

  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 2,
  }).format(numericValue);
};

const normalizeLabel = (value) => String(value || "").trim().toLowerCase();
const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const formatContactName = (phone) =>
  [phone?.contacto, phone?.apellido_contacto]
    .filter(Boolean)
    .map((value) => String(value).trim())
    .filter(Boolean)
    .join(" ");

const getTodayInputDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getManagementErrorMessage = (result, fallback = "No se pudo guardar la gestión.") => {
  const detail =
    result?.resp ||
    result?.data?.resp ||
    result?.data?.error_detalle ||
    result?.data?.error ||
    result?.data?.message ||
    result?.error ||
    result?.message;

  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  return detail.error || detail.message || detail.resp || JSON.stringify(detail);
};

const isManagementErrorResult = (result) => {
  const code = Number(result?.code || result?.statusCode || result?.status || result?.data?.status || 0);
  return (
    code >= 400 ||
    [1, true, "1", "true"].includes(result?.data?.error) ||
    result?.success === false ||
    result?.data?.success === false ||
    result?.data?.error_detalle?.success === false
  );
};

const ManagementModal = ({ prospect, onClose, loading, onSave, saving }) => {
  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [promiseDate, setPromiseDate] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [selectedPhoneId, setSelectedPhoneId] = useState("");

  useEffect(() => {
    setSelectedOptionId("");
    setPromiseDate("");
    setAdditionalInfo("");
    setSelectedPhoneId("");
  }, [prospect?.id]);

  if (!prospect) return null;

  const selectedOption = prospect.options?.find((option) => String(option.id) === String(selectedOptionId));
  const selectedPhone = prospect.phones?.find((phone) => String(phone.id) === String(selectedPhoneId));
  const requiresPromiseDate = normalizeLabel(selectedOption?.texto) === normalizeLabel("Promesa de Pago");
  const todayInputDate = getTodayInputDate();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="relative flex w-full max-w-6xl max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Gestionar prospecto</h2>
            <p className="text-sm text-slate-500">
              {prospect.campaign?.nombre || "Sin campaña"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Cerrar
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="space-y-5 p-6">
            {prospect.warning ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {prospect.warning}
              </div>
            ) : null}

            {prospect.previousManagement ? (
              <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">Gestión anterior</p>
                    <h3 className="mt-2 text-lg font-bold text-amber-950">
                      {prospect.previousManagement.resultado || "Sin resultado"}
                    </h3>
                    <p className="mt-1 text-sm text-amber-900">
                      Fecha: {formatDateTime(prospect.previousManagement.fecha_gestion)} · Teléfono: {prospect.previousManagement.telefono_gestionado || "-"}
                    </p>
                  </div>
                  {prospect.previousManagement.recycled_at ? (
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-amber-700">
                      Reagendado: {formatDateTime(prospect.previousManagement.recycled_at)}
                    </span>
                  ) : null}
                </div>
                {prospect.previousManagement.observaciones ? (
                  <p className="mt-3 rounded-lg bg-white/70 px-3 py-2 text-sm text-amber-950">
                    {prospect.previousManagement.observaciones}
                  </p>
                ) : null}
              </section>
            ) : null}

            <section className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-5 shadow-sm">
              <div className="grid gap-4 lg:grid-cols-[1.5fr_0.9fr]">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Socio</p>
                  <h3 className="mt-2 text-2xl font-bold text-slate-800">
                    {[prospect.client?.nombre, prospect.client?.apellido].filter(Boolean).join(" ") || "Sin datos del socio"}
                  </h3>
                  <p className="mt-2 text-sm font-semibold text-slate-600">
                    Documento: {prospect.client?.documento || "-"} · ID socio: {prospect.id_externo || "-"} · Email: {prospect.client?.email || "-"}  
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
                  <DetailItem label="Estado del socio" value={prospect.client?.estado} />
                  <DetailItem label="Sucursal / Oferta" value={[prospect.sucursal, prospect.oferta].filter(Boolean).join(" · ")} />
                </div>
              </div>
            </section>

            <div className="grid gap-5 xl:grid-cols-[1.2fr_0.8fr]">
              <section className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-800">Speech</h3>
                <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-3">
                    <p className="font-semibold text-slate-700">{prospect.speech?.titulo || "Sin speech asignado"}</p>
                    {prospect.speech?.blocks?.length ? (
                      <p className="mt-1 text-xs text-slate-500">
                        Bloques aplicados: {prospect.speech.blocks.map((block) => block.titulo).join(" · ")}
                      </p>
                    ) : null}
                  </div>
                  {prospect.speech?.html ? (
                    <div
                      className="prose prose-sm max-w-none break-words text-slate-700"
                      dangerouslySetInnerHTML={{ __html: prospect.speech.html }}
                    />
                  ) : (
                    <p className="text-sm text-slate-500">Esta campaña no tiene un speech resuelto para este registro.</p>
                  )}
                </div>
              </section>

              <section className="space-y-5">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Deuda y Oxigeno</h3>
                  <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Deuda total</p>
                        <p className="mt-1 text-2xl font-bold text-emerald-950">{formatCurrency(prospect.account?.deuda_total)}</p>
                      </div>
                      <DetailItem label="Ultimo resumen" value={prospect.account?.ultimo_resumen_numero} />
                    </div>

                    {prospect.account?.opciones_pago?.length ? (
                      <div className="mt-4 grid gap-2 sm:grid-cols-2">
                        {prospect.account.opciones_pago.map((option) => (
                          <div key={option.cuotas} className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                              {option.cuotas} cuota{option.cuotas > 1 ? "s" : ""}
                            </p>
                            <p className="mt-1 text-base font-bold text-emerald-900">{formatCurrency(option.importe)}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-4 text-sm text-slate-500">No hay información de deuda disponible para este socio.</p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Estados de la gestión</h3>
                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    {prospect.options?.length ? (
                      <div className="space-y-3">
                        <label className="block">
                          <span className="mb-2 block text-sm font-medium text-slate-700">Seleccioná el estado</span>
                          <select
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-custom-green-ccard-1 focus:outline-none"
                            value={selectedOptionId}
                            onChange={(event) => {
                              const nextOptionId = event.target.value;
                              setSelectedOptionId(nextOptionId);
                              const nextOption = prospect.options?.find((option) => String(option.id) === String(nextOptionId));
                              if (nextOption?.texto !== "Promesa de Pago") {
                                setPromiseDate("");
                              }
                            }}
                          >
                            <option value="" disabled>
                              Seleccioná un estado de gestión
                            </option>
                            {prospect.options.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.texto}
                              </option>
                            ))}
                          </select>
                        </label>
                        <p className="text-xs text-slate-500">
                          Estados disponibles: {prospect.options.map((option) => option.texto).join(" · ")}
                        </p>
                        {requiresPromiseDate ? (
                          <label className="block rounded-lg border border-amber-200 bg-amber-50 p-3">
                            <span className="mb-2 block text-sm font-medium text-slate-700">Fecha prometida de pago</span>
                            <input
                              type="date"
                              min={todayInputDate}
                              value={promiseDate}
                              onChange={(event) => setPromiseDate(event.target.value)}
                              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-custom-green-ccard-1 focus:outline-none"
                            />
                          </label>
                        ) : null}
                        <div>
                          <span className="mb-2 block text-sm font-medium text-slate-700">Teléfono trabajado</span>
                          {prospect.phones?.length ? (
                            <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                              {prospect.phones.map((phone) => {
                                const contactName = formatContactName(phone);
                                const isSelectedPhone = String(selectedPhoneId) === String(phone.id);

                                return (
                                  <label
                                    key={phone.id}
                                    className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 transition-colors ${
                                      isSelectedPhone
                                        ? "border-custom-green-ccard-1 bg-emerald-50/70"
                                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                                    }`}
                                  >
                                    <input
                                      type="radio"
                                      name={`telefono-${prospect.id}`}
                                      className="mt-1 h-4 w-4 accent-custom-green-ccard-1"
                                      value={phone.id}
                                      checked={isSelectedPhone}
                                      onChange={(event) => setSelectedPhoneId(event.target.value)}
                                    />
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="break-all text-sm font-bold text-slate-900">{phone.telefono}</span>
                                        {phone.tipo_contacto ? (
                                          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                                            {phone.tipo_contacto}
                                          </span>
                                        ) : null}
                                        {phone.es_celular ? (
                                          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">Celular</span>
                                        ) : null}
                                        {phone.activo ? (
                                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Activo</span>
                                        ) : (
                                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Inactivo</span>
                                        )}
                                      </div>

                                      {(contactName || phone.ultimo_contacto || phone.observaciones) ? (
                                        <div className="mt-2 space-y-1 text-xs text-slate-600">
                                          {contactName ? (
                                            <p className="font-medium text-slate-700">
                                              Contacto: <span className="font-semibold">{contactName}</span>
                                            </p>
                                          ) : null}
                                          {phone.ultimo_contacto ? (
                                            <p>Último contacto: {phone.ultimo_contacto}</p>
                                          ) : null}
                                          {phone.observaciones ? (
                                            <p className="break-words text-slate-500">{phone.observaciones}</p>
                                          ) : null}
                                        </div>
                                      ) : null}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3 text-sm text-slate-500">
                              No hay teléfonos disponibles para este socio.
                            </div>
                          )}
                        </div>
                        <label className="block">
                          <span className="mb-2 block text-sm font-medium text-slate-700">Información adicional</span>
                          <textarea
                            value={additionalInfo}
                            onChange={(event) => setAdditionalInfo(event.target.value)}
                            rows={4}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-custom-green-ccard-1 focus:outline-none"
                            placeholder="Agregá observaciones o contexto de la gestión"
                          />
                        </label>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => onSave({
                            id_opcion_respuesta: selectedOptionId,
                            fecha_promesa_pago: promiseDate,
                            observaciones: additionalInfo,
                            id_telefono_externo: selectedPhone?.id || null,
                            telefono_gestionado: selectedPhone?.telefono || null,
                          })}
                          className="w-full rounded-lg bg-custom-green-ccard-1 px-4 py-2 text-sm font-semibold text-white hover:bg-custom-green-ccard-2 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {saving ? "Guardando..." : "Guardar gestión"}
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">La campaña no tiene estados de gestión configurados.</p>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-700">Estado actual</p>
                  <p className="mt-2 text-sm text-slate-500">
                    {prospect.isFoundBySearch
                      ? "Este registro ya tenía una gestión previa y fue recuperado por el buscador."
                      : "Este registro aparece porque está asignado a tu usuario y está pendiente de gestión."}
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/60">
            <div className="rounded-lg bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow">
              Cargando información del socio...
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ManagementList = () => {
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [summary, setSummary] = useState({ totalPendientes: 0, campanasActivas: 0 });
  const [operator, setOperator] = useState(null);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingManagement, setSavingManagement] = useState(false);
  const [prospectSearch, setProspectSearch] = useState("");
  const [debouncedProspectSearch, setDebouncedProspectSearch] = useState("");
  const [prospectPage, setProspectPage] = useState(1);
  const [prospectPageSize, setProspectPageSize] = useState(25);
  const [queuePagination, setQueuePagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });

  const fetchQueue = useCallback(async () => {
    const userId = Number(localStorage.getItem("userId"));
    setLoading(true);

    if (!Number.isFinite(userId) || userId <= 0) {
      setLoading(false);
      Swal.fire("Sesión inválida", "No se pudo identificar al operador actual.", "warning");
      return;
    }

    try {
      const response = await ProspectsService.getManagementQueue(
        userId,
        selectedCampaignId,
        debouncedProspectSearch,
        prospectPage,
        prospectPageSize
      );
      setQueue(response.data?.queue || []);
      setCampaigns(response.data?.campaigns || []);
      setSelectedCampaignId(response.data?.selectedCampaignId ? String(response.data.selectedCampaignId) : "");
      setSummary(response.data?.summary || { totalPendientes: 0, campanasActivas: 0 });
      setQueuePagination(response.data?.pagination || { page: prospectPage, pageSize: prospectPageSize, total: 0, totalPages: 1 });
      setOperator(response.data?.operator || null);
    } catch {
      setQueue([]);
      setCampaigns([]);
      setSummary({ totalPendientes: 0, campanasActivas: 0 });
      setQueuePagination({ page: 1, pageSize: prospectPageSize, total: 0, totalPages: 1 });
      setOperator(null);
      Swal.fire("Error", "No se pudo cargar la bandeja de gestión de prospectos.", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedCampaignId, debouncedProspectSearch, prospectPage, prospectPageSize]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProspectSearch(prospectSearch);
      setProspectPage(1);
    }, 180);

    return () => clearTimeout(timer);
  }, [prospectSearch]);

  useEffect(() => {
    setProspectPage(1);
    setProspectSearch("");
    setDebouncedProspectSearch("");
  }, [selectedCampaignId]);

  const searchableQueue = useMemo(
    () => queue.map((item) => ({
      ...item,
      __searchText: normalizeSearchText([
        item.id_externo,
        item.sucursal,
        item.oferta,
        item.prioridad,
        item.campaign?.nombre,
        item.previousManagement?.resultado,
        item.previousManagement?.telefono_gestionado,
        item.searchText,
      ].filter(Boolean).join(" ")),
    })),
    [queue]
  );

  const filteredQueue = searchableQueue;

  const prospectTotalPages = Math.max(queuePagination.totalPages || 1, 1);

  useEffect(() => {
    setProspectPage((current) => Math.min(current, prospectTotalPages));
  }, [prospectTotalPages]);

  const paginatedQueue = filteredQueue;

  const columns = useMemo(
    () => [
      {
        Header: "Agenda",
        accessor: (row) => {
          if (row.isFoundBySearch) return row.previousManagement?.resultado || "Gestionado";
          return row.isAutomaticAgenda ? (row.previousManagement?.resultado || "Reagendado") : "Pendiente";
        },
        id: "agenda",
        Cell: ({ row }) => {
          if (row.original.isFoundBySearch) {
            return (
              <span className="rounded-full bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-700">
                Encontrado por búsqueda
              </span>
            );
          }

          if (!row.original.isAutomaticAgenda) {
            return (
              <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                Nuevo
              </span>
            );
          }

          const label = row.original.previousManagement?.resultado || "Reagendado";
          const isPromise = normalizeLabel(label) === normalizeLabel("Promesa de Pago");

          return (
            <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
              isPromise ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"
            }`}>
              {label}
            </span>
          );
        },
      },
      {
        Header: "Campaña",
        accessor: (row) => row.campaign?.nombre || "-",
        id: "campaign",
      },
      {
        Header: "ID",
        accessor: "id_externo",
      },
      {
        Header: "Sucursal",
        accessor: "sucursal",
      },
      {
        Header: "Oferta",
        accessor: "oferta",
      },
      {
        Header: "Acciones",
        id: "acciones",
        disableSortBy: true,
        Cell: ({ row }) => (
          <button
            type="button"
            onClick={async () => {
              const userId = Number(localStorage.getItem("userId"));
              setDetailLoading(true);
              setSelectedProspect(row.original);

              try {
                const response = await ProspectsService.getManagementProspectDetail(row.original.id, userId);
                setSelectedProspect((prev) => ({
                  ...(prev || row.original),
                  ...(response.data || {}),
                }));
              } catch {
                setSelectedProspect((prev) => ({
                  ...(prev || row.original),
                  warning: "No se pudo completar la información del socio desde la API externa.",
                }));
              } finally {
                setDetailLoading(false);
              }
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-custom-green-ccard-1 px-3 py-2 text-sm font-semibold text-white hover:bg-custom-green-ccard-2"
          >
            <EyeIcon className="h-4 w-4" />
            Gestionar
          </button>
        ),
      },
    ],
    []
  );

  const handleSaveManagement = async (formData) => {
    const userId = Number(localStorage.getItem("userId"));

    if (!formData.id_opcion_respuesta) {
      Swal.fire("Información", "El estado de la gestión es obligatorio.", "info");
      return;
    }

    const selectedOption = selectedProspect?.options?.find(
      (option) => String(option.id) === String(formData.id_opcion_respuesta)
    );

    if (selectedOption?.texto === "Promesa de Pago" && !formData.fecha_promesa_pago) {
      Swal.fire("Información", "Seleccioná la fecha de promesa de pago.", "info");
      return;
    }

    if (selectedOption?.texto === "Promesa de Pago" && formData.fecha_promesa_pago < getTodayInputDate()) {
      Swal.fire("Información", "La fecha de promesa de pago no puede ser anterior a la fecha actual.", "info");
      return;
    }

    if ((selectedProspect?.phones?.length || 0) > 0 && !formData.id_telefono_externo) {
      Swal.fire("Información", "Seleccioná el teléfono que se trabajó en la gestión.", "info");
      return;
    }

    setSavingManagement(true);
    try {
      const result = await ProspectsService.saveManagement(selectedProspect.id, {
        userId,
        id_opcion_respuesta: Number(formData.id_opcion_respuesta),
        fecha_promesa_pago: formData.fecha_promesa_pago || null,
        observaciones: formData.observaciones || "",
        id_telefono_externo: formData.id_telefono_externo || null,
        telefono_gestionado: formData.telefono_gestionado || null,
      });

      if (isManagementErrorResult(result)) {
        throw new Error(getManagementErrorMessage(result));
      }

      Swal.fire("Éxito", "La gestión se guardó correctamente.", "success");
      setSelectedProspect(null);
      await fetchQueue();
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.resp || error?.message || "No se pudo guardar la gestión.", "error");
    } finally {
      setSavingManagement(false);
    }
  };

  return (
    <div className="min-h-screen pb-[100px]">
      <SpinningWheel isVisible={loading} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-700">Gestión Prospectos</h1>
        <p className="mt-1 text-sm text-gray-500">
          Bandeja de trabajo del operador con registros asignados y pendientes.
        </p>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
        <StatCard
          icon={ClipboardDocumentCheckIcon}
          label="Pendientes para gestionar"
          value={summary.totalPendientes}
          color="border-custom-green-ccard-1"
        />
        <StatCard
          icon={MegaphoneIcon}
          label="Campañas activas en bandeja"
          value={summary.campanasActivas}
          color="border-custom-yellow-ccard"
        />
        <StatCard
          icon={UserCircleIcon}
          label="Operador"
          value={operator?.screenname || localStorage.getItem("screenname") || "-"}
          color="border-custom-violet-ccard"
        />
      </div>

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-sm text-slate-500">
            {operator
              ? `Operador asignado: ${operator.nombre} · ${operator.sucursal || "Sin sucursal"}`
              : "Tu usuario todavía no está habilitado como colaborador de Prospectos o no tiene registros asignados pendientes."}
          </p>

          {campaigns.length ? (
            <label className="flex w-full flex-col gap-1 text-sm font-medium text-slate-700 lg:w-80">
              Campaña
              <select
                value={selectedCampaignId}
                onChange={(event) => {
                  setSelectedProspect(null);
                  setSelectedCampaignId(event.target.value);
                }}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-custom-green-ccard-1 focus:outline-none"
              >
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.nombre} ({campaign.totalPendientes})
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="search"
              value={prospectSearch}
              onChange={(event) => setProspectSearch(event.target.value)}
              placeholder="Buscar por ID, documento, teléfono, sucursal, oferta o campaña"
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 focus:border-custom-green-ccard-1 focus:outline-none"
            />
          </div>

          <div className="flex flex-col gap-2 text-sm text-slate-600 sm:flex-row sm:items-center">
            <span>
              {queuePagination.total || 0} registros · página {prospectPage} de {prospectTotalPages}
            </span>
            <select
              value={prospectPageSize}
              onChange={(event) => {
                setProspectPageSize(Number(event.target.value));
                setProspectPage(1);
              }}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-custom-green-ccard-1 focus:outline-none"
            >
              <option value={10}>10 por página</option>
              <option value={25}>25 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </select>
          </div>
        </div>
      </section>

      <Table columns={columns} data={paginatedQueue} showGlobalFilter={false} pagination={false} />

      <div className="mt-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500">
          Mostrando {paginatedQueue.length} de {queuePagination.total || 0} registros
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={prospectPage <= 1}
            onClick={() => setProspectPage((current) => Math.max(current - 1, 1))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Anterior
          </button>
          <span className="min-w-[100px] text-center text-sm text-slate-600">
            {prospectPage} / {prospectTotalPages}
          </span>
          <button
            type="button"
            disabled={prospectPage >= prospectTotalPages}
            onClick={() => setProspectPage((current) => Math.min(current + 1, prospectTotalPages))}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      </div>

      <ManagementModal
        prospect={selectedProspect}
        loading={detailLoading}
        saving={savingManagement}
        onSave={handleSaveManagement}
        onClose={() => {
          setSelectedProspect(null);
          setDetailLoading(false);
        }}
      />
    </div>
  );
};

export default ManagementList;
